// src/utils/openAIService.js

import { updateUserSpending } from './TokenUtils';
import { CHAT_LIMITS } from './usageChecker';


// Simple in-memory cache to minimize DB lookups
const usageCache = {
  data: {},
  isExceeded(userId) {
    const entry = this.data[userId];
    if (!entry) return false;
    if (Date.now() - entry.lastChecked > 5 * 60 * 1000) {
      delete this.data[userId];
      return false;
    }
    return entry.exceeded;
  },
  update(userId, weekly, monthly) {
    const exceeded = weekly > CHAT_LIMITS.WEEKLY || monthly > CHAT_LIMITS.MONTHLY;
    this.data[userId] = {
      weekly,
      monthly,
      lastChecked: Date.now(),
      exceeded,
      limitType: weekly > CHAT_LIMITS.WEEKLY ? 'weekly' : 'monthly'
    };
    return exceeded;
  }
};

const CHAT_PROXY_URL = process.env.REACT_APP_CHAT_PROXY_URL;
const OPENAI_MODEL_ID = 'gpt-4.1-mini';

// Background usage check (non-blocking)
function checkUsageBackground(userId, dynamoUtils) {
  if (!userId || !dynamoUtils) return;
  (async () => {
    try {
      const profile = await dynamoUtils.getUserProfile(userId);
      if (!profile || !profile.spending) return;
      usageCache.update(userId, profile.spending.weekly_spend, profile.spending.monthly_spend);
    } catch (err) {
      console.error('Error checking OpenAI usage in background:', err);
    }
  })();
}

function checkUsageLimits(userId, dynamoUtils) {
  if (!userId) return;
  if (usageCache.isExceeded(userId)) {
    const entry = usageCache.data[userId];
    const limitType = entry.limitType || 'monthly';
    throw new Error(`Usage limit exceeded: You have reached your ${limitType} usage limit. Please contact support for more information.`);
  }
  if (dynamoUtils) checkUsageBackground(userId, dynamoUtils);
}

/**
 * Streams chat completions from the OpenAI proxy.
 * @param {Object} params
 * @param {string} params.token - The Cognito JWT.
 * @param {Array} params.messages - Array of messages in OpenAI format [{role: 'system', content: '...'}, ...].
 * @param {function} params.onChunk - Callback for each text chunk: (textChunk: string) => void.
 * @param {function} params.onComplete - Callback when stream is complete, optionally with usage data: (usage: object | null) => void.
 * @param {function} params.onError - Callback for errors: (error: Error) => void.
 * @param {Object} params.stopGenerationSignal - Ref object { current: boolean } to signal stopping.
 * @param {string} params.userId - The user ID for token usage tracking.
 */
export const streamOpenAIChatCompletion = async ({
  token,
  messages,
  tools = [], // Optional tools array, default to empty if not provided
  tool_choice = 'auto', // Default to 'auto' for OpenAI to decide tool use
  onChunk,
  onComplete, // Modified to potentially receive usage
  onError,
  stopGenerationSignal,
  userId, // This userId is the one from your Cognito token's 'sub'
  dynamoUtils
}) => {
  if (!CHAT_PROXY_URL) {
    onError(new Error("OpenAI Chat proxy URL is not configured."));
    if (typeof onComplete === 'function') onComplete(null); // Ensure onComplete is called
    return;
  }

  let controller = new AbortController();
  const { signal } = controller;
  let usageDataFromStream = null; // To store usage data if received
  let finishReasonFromStream = null; // To store finish reason if received
  // Check usage limits without blocking
  try {
    checkUsageLimits(userId, dynamoUtils);
  } catch (err) {
    onError(err);
    if (typeof onComplete === "function") onComplete(null);
    return;
  }


  const checkStopInterval = setInterval(() => {
    if (stopGenerationSignal && stopGenerationSignal.current) {
      console.log('[openAIService] stopGenerationSignal detected, aborting fetch.');
      controller.abort();
      clearInterval(checkStopInterval);
    }
  }, 100);

  try {
    const response = await fetch(CHAT_PROXY_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messages,
        tools,
        tool_choice, // Use 'auto' to let OpenAI decide if tool use is needed
      }),
      signal,
    });

    clearInterval(checkStopInterval);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[openAIService] Proxy request failed (${response.status}):`, errorText);
      onError(new Error(`OpenAI Chat Proxy request failed: ${response.status} ${errorText}`));
      if (typeof onComplete === 'function') onComplete(null);
      return;
    }

    if (!response.body) {
      onError(new Error("Response body is null"));
      if (typeof onComplete === 'function') onComplete(null);
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let doneReading = false;

    console.log("[openAIService] Stream connection established. Reading response...");

    while (!doneReading) {
      if (stopGenerationSignal?.current) { // Check again before read
        console.log('[openAIService] Stream reading loop: stop signal detected.');
        if (!reader.closed) {
          await reader.cancel('User requested stop').catch(e => console.warn("[openAIService] Error cancelling reader:", e));
        }
        break;
      }

      let value, done;
      try {
        ({ value, done } = await reader.read());
      } catch (readError) {
        if (readError.name === 'AbortError') {
          console.log('[openAIService] Reader aborted during read.');
        } else {
          onError(readError);
        }
        doneReading = true;
        break;
      }
      
      if (done) {
        console.log("[openAIService] Stream finished (reader.read() done).");
        doneReading = true;
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      // console.log("[openAIService] Buffer content:", buffer.substring(0, 200)); // For debugging buffer

      let eolIndex;
      while ((eolIndex = buffer.indexOf('\n\n')) >= 0) {
        const line = buffer.substring(0, eolIndex).trim();
        buffer = buffer.substring(eolIndex + 2);

        if (line.startsWith('data: ')) {
          const dataContent = line.substring(5).trim();
          // console.log("[openAIService] SSE Data Received:", dataContent.substring(0,100)); // For debugging SSE events

          if (dataContent === '[DONE]') {
            console.log("[openAIService] Received [DONE] event.");
            doneReading = true; // Mark as done, the usage event might follow
            // Do not break the outer while loop yet, wait for potential usage event
            continue; // Continue to process more events in this chunk if any
          }

          try {
            const parsed = JSON.parse(dataContent);
  
            // Capture finish_reason if present in any chunk
            if (parsed.choices && parsed.choices[0]?.finish_reason) {
              finishReasonFromStream = parsed.choices[0].finish_reason;
              console.log('[openAIService] Captured finish_reason:', finishReasonFromStream);
            }

            // Check for the usage event (OpenAI specific structure)
            if (parsed.usage && typeof parsed.usage.total_tokens === 'number') {
              usageDataFromStream = {
                inputTokens: parsed.usage.prompt_tokens,
                outputTokens: parsed.usage.completion_tokens,
                totalTokens: parsed.usage.total_tokens,
              };
              if (!finishReasonFromStream && parsed.choices && parsed.choices[0]?.finish_reason) {
                 finishReasonFromStream = parsed.choices[0].finish_reason;
              }
                doneReading = true;
                break;
              // The stream is now effectively complete regarding content.
              // The [DONE] event usually precedes this or comes very close.
            }
            // Regular content chunk
            else if (parsed.choices && parsed.choices[0]?.delta) {
              onChunk(parsed);
            }
            // Other potential stream events (e.g. Azure OpenAI adds 'context')
            // else if (parsed.object === "chat.completion.chunk") {
              // This is a standard chunk, but if it's not content and not usage, log it
              // console.log("[openAIService] Received non-content, non-usage chunk:", parsed);
            // }

          } catch (e) {
            console.error('[openAIService] Error parsing OpenAI SSE JSON chunk:', e, "Problematic line:", line);
          }
        }
      }
    }

    if (buffer.trim()) {
        console.warn('[openAIService] Stream ended with unprocessed data in buffer:', buffer);
    }

  } catch (fetchError) {
    clearInterval(checkStopInterval); // Ensure interval is cleared
    if (fetchError.name === 'AbortError') {
      console.log('[openAIService] Fetch operation was aborted.');
    } else {
      console.error('[openAIService] Fetch error:', fetchError);
      onError(fetchError);
    }
  } finally {
    clearInterval(checkStopInterval);
    console.log("[openAIService] Stream processing finished. Final usage data:", usageDataFromStream, "Finish Reason:", finishReasonFromStream);
    if (typeof onComplete === 'function') {
      onComplete(usageDataFromStream, finishReasonFromStream); // <--- PASS finishReasonFromStream
    }
  }
};

/**
 * Generate short suggested next responses using the OpenAI chat proxy.
 * This reuses the streaming endpoint but buffers all text until complete.
 * @param {Object} params
 * @param {string} params.token - Auth token for the proxy
 * @param {Array} params.recentMessages - Recent chat messages
 * @param {string} params.userId - Cognito user ID for token tracking
 * @param {Object} [params.dynamoUtils] - DynamoDB utils for cost tracking
 * @returns {Promise<Array>} Array of suggestion strings
 */
export async function generateSuggestedResponses({ token, recentMessages, userId, dynamoUtils }) {
  let fullText = '';
  const limitedMessages = recentMessages.slice(-4);
  if (userId) {
    try {
      checkUsageLimits(userId, dynamoUtils);
    } catch (err) {
      console.warn(`Cannot generate suggested responses: ${err.message}`);
      return [];
    }
  }
  const conversationText = limitedMessages.map(m => {
    const role = m.role.toUpperCase();
    let content = '';
    if (typeof m.content === 'string') {
      content = m.content;
    } else if (Array.isArray(m.content)) {
      content = m.content.filter(c => c.type === 'text').map(c => c.text).join('\n');
    }
    return `${role}: ${content}`;
  }).join('\n\n');

  const suggestionsPrompt = `You're helping generate suggested responses for a student in a math tutoring session.

CONVERSATION CONTEXT:
${conversationText}

TASK:
Generate 3 short suggested responses (5-15 words each) the student could send next.

GUIDELINES:
- Responses should be helpful continuations of the conversation
- NEVER suggest actual answers to math problems
- Focus on asking for clarification, examples, or different explanations
- Make responses conversational and natural
- Each suggestion should serve a different purpose

EXAMPLE PATTERNS:
- "Can you explain that step by step?"
- "I'm still confused about..."
- "Can you show me another example?"
- "How does this relate to [relevant topic]?"
- "What's the formula for this?"
- "Could you draw/graph this for me?"
- "Why is this approach better?"
- "Can you explain this in simpler terms?"
- "How would I apply this to..."
- "Let me try solving the next one"

Return ONLY valid JSON in this format:
{"suggestions": ["suggestion1", "suggestion2", "suggestion3"]}`;

  return new Promise((resolve) => {
    streamOpenAIChatCompletion({
      token,
      messages: [
        { role: 'system', content: 'You are a helpful assistant that generates short, relevant suggested responses for students in a math tutoring session. Return only JSON.' },
        { role: 'user', content: suggestionsPrompt }
      ],
      onChunk: (chunk) => {
        const delta = chunk.choices?.[0]?.delta;
        if (delta?.content) {
          fullText += delta.content;
        }
      },
      onComplete: async (usageData) => {
        let suggestions = [];
        try {
          const match = fullText.match(/{[\s\S]*}/);
          if (match) {
            const parsed = JSON.parse(match[0]);
            suggestions = parsed.suggestions || [];
          }
        } catch (err) {
          console.error('[openAIService] Error parsing suggestions JSON:', err);
        }

        if (usageData && userId) {
          try {
            if (dynamoUtils) {
              await updateUserSpending(usageData, userId, OPENAI_MODEL_ID, dynamoUtils, 'Suggested Responses');
            } else {
              console.warn('No dynamoUtils available for token usage tracking');
            }
          } catch (err) {
            console.warn('[openAIService] Error tracking token usage for suggestions:', err);
          }
        }
        resolve(suggestions);
      },
      onError: (err) => {
        console.error('[openAIService] Error generating suggested responses:', err);
        resolve([]);
      },
      stopGenerationSignal: { current: false },
      userId
    });
  });
}

/**
 * Grade a student's exam answer using the OpenAI proxy.
 * Returns an object like {correct: boolean, feedback: string}
 */
export async function gradeExamAnswer({ token, question, correctAnswer, userAnswer, revealAnswer = false, userId, dynamoUtils }) {
  let fullText = '';

  const instructions = revealAnswer
    ? 'Provide helpful feedback for the student and include the correct answer.'
    : 'Provide helpful feedback for the student without revealing the correct answer.';
  const jsonExample = revealAnswer
    ? '{"correct": true, "feedback": "...", "answer": "..."}'
    : '{"correct": true, "feedback": "..."}';

  const prompt = `You are marking a mathematics exam.\n\nQUESTION:\n${question}\n\nCORRECT ANSWER:\n${correctAnswer}\n\nSTUDENT ANSWER:\n${userAnswer}\n\n${instructions}\nRespond only in JSON like ${jsonExample}.`;

  return new Promise((resolve) => {
    streamOpenAIChatCompletion({
      token,
      messages: [
        { role: 'system', content: 'You are a strict but encouraging exam marker. Return concise JSON only.' },
        { role: 'user', content: prompt }
      ],
      onChunk: (chunk) => {
        const delta = chunk.choices?.[0]?.delta;
        if (delta?.content) {
          fullText += delta.content;
        }
      },
      onComplete: async (usageData) => {
        let result = { correct: false, feedback: '' };
        try {
          const match = fullText.match(/{[\s\S]*}/);
          if (match) {
            result = JSON.parse(match[0]);
          }
        } catch (err) {
          console.error('[openAIService] Error parsing grading JSON:', err);
        }
        if (revealAnswer && !result.answer) {
          result.answer = correctAnswer;
        }

        if (usageData && userId) {
          try {
            if (dynamoUtils) {
              await updateUserSpending(usageData, userId, OPENAI_MODEL_ID, dynamoUtils, 'Exam Marking');
            }
          } catch (err) {
            console.warn('[openAIService] Error tracking token usage for exam marking:', err);
          }
        }
        resolve(result);
      },
      onError: (err) => {
        console.error('[openAIService] Error grading exam answer:', err);
        resolve({ correct: false, feedback: 'Error grading answer.' });
      },
      stopGenerationSignal: { current: false },
      userId
    });
  });
}
